import { StringType } from 'types/global'
import { OtherComponentType } from 'types/otherComponent'
import { doNotTranslate } from 'util/misc'
import {
  BASELINE_COPPER_TEMP,
  CABLE_STC_TEMP,
  COPPER_RESISTIVITY,
  COPPER_TEMP_COEFFICIENT,
  DEFAULT_CABLE_TEMP,
  DEFAULT_PANEL_TEMP_INCREASE,
  GOOD_VOLTAGE_DROP,
  MAX_ALLOWABLE_VOLTAGE_DROP,
  MAX_CABLE_SIZE,
  STANDARD_CABLE_SIZES,
} from './constants'
import type { ElectricalIssueCode } from './electricalIssueCodes'
import { resultsType } from './types'

const CABLE_THICKNESS_CACHE = new Map<string, number>()
const MAX_CACHE_SIZE = 20

function getCacheKey(length: number, phase: compatiblePhases, current: number, qty: number): string {
  return `${Math.round(length * 10) / 10}-${phase}-${Math.round(current * 10) / 10}-${qty}`
}

export function getSupplyVoltage(supplyPhase: string) {
  const supplyVoltage = supplyPhase === 'single_phase' ? 240 : 415
  return supplyVoltage
}

export function getDCStringVoltage(cableLocation: any): number | null {
  try {
    const module = window.AccountHelper.getModuleById(window.editor.selectedSystem.moduleType().id)
    if (!module || !module.max_power_voltage || !module.temp_coefficient_voc) {
      return null
    }

    const project = window.WorkspaceHelper?.project
    if (!project) {
      return null
    }

    const [minTemp, maxTemp] = window.Utils.getMinMaxTemperature(project)
    if (!minTemp || !maxTemp) {
      return null
    }

    // Calculate operating temperature (max ambient + panel temp increase)
    const maxPanelOperatingTemp = maxTemp + (window.Utils.AVERAGE_PANEL_TEMP_INCREASE || DEFAULT_PANEL_TEMP_INCREASE)

    const vmpAtOperatingTemp = window.Utils.getPanelSpecAtTemp(
      module.max_power_voltage,
      module.temp_coefficient_voc,
      maxPanelOperatingTemp
    )

    let panelsPerString: number

    if (cableLocation.type === 'OsString') {
      panelsPerString = cableLocation.moduleQuantity()
    } else if (cableLocation.type === 'OsMppt') {
      // For an MPPT, get panels per string from the first string
      // (all strings in an MPPT should have the same number of panels)
      const strings = cableLocation.strings()
      if (strings.length === 0) {
        return null
      }
      panelsPerString = strings[0].moduleQuantity()
    } else if (cableLocation.type === 'OsInverter') {
      const mppts = cableLocation.mppts()
      if (mppts.length === 0) {
        return null
      }
      const strings = mppts[0].strings()
      if (strings.length === 0) {
        return null
      }
      panelsPerString = strings[0].moduleQuantity()
    } else {
      return null
    }

    if (!Number.isFinite(panelsPerString) || panelsPerString <= 0) {
      return null
    }

    return vmpAtOperatingTemp * panelsPerString
  } catch (e) {
    console.warn('Error calculating DC string voltage:', e)
    return null
  }
}

export function calculateCableResistance(
  crossSectionArea: number, // in mm²
  temperature: number = DEFAULT_CABLE_TEMP // in °C
): number {
  // Convert mm² to m²
  const areaInSquareMeters = crossSectionArea * 1e-6

  // Calculate resistivity at the given temperature
  const resistivityAtTemperature =
    COPPER_RESISTIVITY * (1 + COPPER_TEMP_COEFFICIENT * (temperature - BASELINE_COPPER_TEMP))

  // Calculate resistance for 1m length
  const resistance = resistivityAtTemperature / areaInSquareMeters

  return Number(resistance.toFixed(5))
}

export function getMeterCompatibility(meter: OtherComponentType, phase: 'single_phase' | 'three_phase'): resultsType {
  const meterPhase = meter.phase_type
  const compatibility = phase === meterPhase ? 'good' : 'bad'
  return {
    compatibility,
    data: {
      meterPhase,
      phase,
    },
  }
}

export function getMaxCableCurrent(cableLocation: any) {
  // Where cableLocation is OsInverter or OsString or OsMppt
  var current
  const module = window.AccountHelper.getModuleById(window.editor.selectedSystem.moduleType().id)
  // Calculate the highest expected Imp, where module.imp is at STC (25 degrees C)
  // Assume the highest expected location temperature is 40, making the temperature difference 15 degrees C
  // Assume that all modules are the same. If a combination of different modules are used in the future, the code should be updated to use the panel with the lowest current in the string
  const temperature_difference = DEFAULT_CABLE_TEMP - CABLE_STC_TEMP
  const impAt40 = module.imp + (temperature_difference * module.temp_coefficient_isc * module.imp) / 100
  // if string, as below. If microinverter, just multiple current by number of modules attached to location.
  const inverter =
    cableLocation.type === 'OsString'
      ? cableLocation.parent.parent
      : cableLocation.type === 'OsMppt'
      ? cableLocation.parent
      : cableLocation

  if (inverter.microinverter) {
    const modulesInScope =
      typeof cableLocation?.moduleQuantity === 'function' ? Number(cableLocation.moduleQuantity()) : undefined
    const modulesPerMicro = Number(inverter?.mppt_quantity)
    const microinverterQty =
      Number.isFinite(modulesInScope) &&
      modulesInScope !== undefined &&
      modulesInScope > 0 &&
      Number.isFinite(modulesPerMicro) &&
      modulesPerMicro > 0
        ? Math.ceil(modulesInScope / modulesPerMicro)
        : undefined
    const inverterOutputCurrent = Number(inverter?.current_ac_max)

    // For microinverters, current should scale with how many micro units feed this cable segment.
    if (Number.isFinite(inverterOutputCurrent) && microinverterQty !== undefined) {
      current = inverterOutputCurrent * microinverterQty
    } else if (cableLocation.type === 'OsInverter' && Number.isFinite(inverterOutputCurrent)) {
      current = inverterOutputCurrent
    } else {
      // Fallback to historical behavior if scope metadata is incomplete.
      current = impAt40 * (Number.isFinite(modulesInScope) && modulesInScope !== undefined ? modulesInScope : 0)
    }
  } else if (cableLocation.type === 'OsInverter') {
    current = cableLocation.current_ac_max
  } else {
    if (cableLocation.type === 'OsString') {
      current = impAt40
    } else {
      // Assume that all modules are the same. If a combination of different modules are used in the future, the code should be updated to add each string's current
      const strings = cableLocation.strings().length
      current = impAt40 * strings
    }
  }
  return current
}

export type compatiblePhases = 'single_phase' | 'three_phase'

export function getThinnestCompatibleCableThickness(
  cableLength: number,
  supplyPhase: compatiblePhases,
  current: number,
  qty: number = 1,
  dcVoltage?: number | null // Optional DC voltage for DC cables
): number {
  // For DC cables, use DC voltage in cache key to avoid incorrect cache hits
  const cacheKey =
    dcVoltage !== undefined && dcVoltage !== null
      ? `${Math.round(cableLength * 10) / 10}-dc-${dcVoltage}-${Math.round(current * 10) / 10}-${qty}`
      : getCacheKey(cableLength, supplyPhase, current, qty)

  if (CABLE_THICKNESS_CACHE.has(cacheKey)) {
    return CABLE_THICKNESS_CACHE.get(cacheKey)!
  }

  const supplyVoltage = dcVoltage !== undefined && dcVoltage !== null ? dcVoltage : getSupplyVoltage(supplyPhase)

  // For DC cables, phase factor is 2 (round trip). For AC, use phase-specific factor
  const phaseFactor =
    dcVoltage !== undefined && dcVoltage !== null ? 2 : supplyPhase === 'three_phase' ? Math.sqrt(3) : 2
  // Size for GOOD_VOLTAGE_DROP (1%), not MAX_ALLOWABLE_VOLTAGE_DROP (3%). 3% is the
  // statutory ceiling; 1% is the engineering best-practice target. Sizing to 1%
  // means auto-selected cables land in the 'good' band (green) by default rather
  // than 'marginal' (amber). Installers can manually downsize a cable if they
  // want to trade margin for cost.
  const maxResistance = (supplyVoltage * GOOD_VOLTAGE_DROP) / (100 * phaseFactor * cableLength * current * qty)

  // Find first standard size that meets resistance requirement
  const compatibleSize = STANDARD_CABLE_SIZES.find((size) => {
    const resistance = calculateCableResistance(size)
    return resistance < maxResistance
  })

  if (!compatibleSize) {
    console.warn(
      `No standard cable size meets requirements: ${current}A, ${cableLength}m, ${
        dcVoltage !== null ? 'DC' : supplyPhase
      }. Using maximum ${MAX_CABLE_SIZE}.`
    )
  }

  const result = compatibleSize || MAX_CABLE_SIZE

  if (CABLE_THICKNESS_CACHE.size >= MAX_CACHE_SIZE) {
    const first = CABLE_THICKNESS_CACHE.keys().next()
    if (!first.done && first.value !== undefined) {
      CABLE_THICKNESS_CACHE.delete(first.value)
    }
  }
  CABLE_THICKNESS_CACHE.set(cacheKey, result)

  return result
}

// Note cable voltage drop for microinverters DC cabling is not necessary, since the cable lengths are so short.
export function getCableVoltageDrop(
  cable: OtherComponentType | null,
  cableLength: number,
  supplyPhase: 'single_phase' | 'three_phase',
  current: number,
  dcVoltage?: number | null
): resultsType {
  if (!current) {
    return {
      compatibility: null,
      data: {
        error:
          doNotTranslate('Cannot calculate cable voltage drop') +
          ': ' +
          doNotTranslate('component current not defined'),
        issueCode: 'CABLE_VDROP_MAX_CURRENT_UNDEFINED' satisfies ElectricalIssueCode,
      },
    }
  }
  if (!cable) {
    return {
      compatibility: null,
      data: {
        error: doNotTranslate('No cable component selected'),
        issueCode: 'CABLE_VDROP_NO_CABLE_SELECTED' satisfies ElectricalIssueCode,
      },
    }
  }
  if (!cable.cable_thickness) {
    return {
      compatibility: null,
      data: {
        error:
          doNotTranslate('Cannot calculate cable voltage drop') +
          ': ' +
          doNotTranslate('component cable thickness not defined'),
        issueCode: 'CABLE_VDROP_CABLE_THICKNESS_UNDEFINED' satisfies ElectricalIssueCode,
      },
    }
  }
  const supplyVoltage = dcVoltage !== undefined && dcVoltage !== null ? dcVoltage : getSupplyVoltage(supplyPhase)

  if (supplyVoltage <= 0) {
    return {
      compatibility: null,
      data: {
        error:
          doNotTranslate('Cannot calculate cable voltage drop') +
          ': ' +
          doNotTranslate('supply voltage not defined'),
        issueCode: 'CABLE_VDROP_SUPPLY_VOLTAGE_UNDEFINED' satisfies ElectricalIssueCode,
      },
    }
  }

  const cableResistance = calculateCableResistance(cable.cable_thickness)
  const voltageDrop = 2 * cableLength * current * cableResistance
  const voltageDropPercentage = (voltageDrop / supplyVoltage) * 100
  const compatibility =
    voltageDropPercentage < GOOD_VOLTAGE_DROP
      ? 'good'
      : voltageDropPercentage < MAX_ALLOWABLE_VOLTAGE_DROP
      ? 'ok'
      : 'bad'
  return {
    compatibility,
    data: {
      voltageDropPercentage,
    },
  }
}

export function getACIsolatorOutputCurrent(isolatorLocation: any): number | null {
  const inverter = isolatorLocation?.type === 'OsMppt' ? isolatorLocation.parent : isolatorLocation
  const inverterCurrent = Number(inverter?.current_ac_max)
  if (!Number.isFinite(inverterCurrent) || inverterCurrent <= 0) return null

  if (inverter?.microinverter) {
    // isolatorLocation can be the inverter or the branch (MPPT). For branch-level
    // isolators, combine output currents of microinverters within that branch only.
    const moduleQty = isolatorLocation?.moduleQuantity?.()
    const mpptQty = Number(inverter.mppt_quantity)
    if (!Number.isFinite(moduleQty) || !Number.isFinite(mpptQty) || mpptQty <= 0) return null
    return inverterCurrent * Math.ceil(moduleQty / mpptQty)
  }

  return inverterCurrent
}

export function getACIsolatorCompatibility(isolator: OtherComponentType, isolatorLocation: any): resultsType {
  const errors: string[] = []
  var currentCompatibility, voltageCompatibility, phaseCompatibility
  const inverter = isolatorLocation.type === 'OsMppt' ? isolatorLocation.parent : isolatorLocation
  const current = getACIsolatorOutputCurrent(isolatorLocation)
  const currentRating = isolator?.current_rating
  if (current && currentRating) {
    // 25% headroom threshold matches the DC isolator rule: passes with
    // comfortable margin = good, passes with <25% margin = ok (marginal),
    // doesn't pass = bad.
    currentCompatibility = current * 1.25 <= currentRating ? 'good' : current < currentRating ? 'ok' : 'bad'
  } else {
    if (!currentRating) errors.push(doNotTranslate('isolator current rating not defined'))
    if (!current) errors.push(doNotTranslate('maximum inverter output current not defined'))
  }

  const phase = inverter?.phase_type
  const voltage = phase ? getSupplyVoltage(phase) : undefined
  const voltageRating = isolator?.voltage_rating
  if (voltage && voltageRating) {
    voltageCompatibility = voltage * 1.25 <= voltageRating ? 'good' : voltage <= voltageRating ? 'ok' : 'bad'
  } else {
    if (!voltageRating) errors.push(doNotTranslate('isolator voltage rating not defined'))
    if (!voltage) errors.push(doNotTranslate('supply voltage not defined'))
  }

  const isolatorPhase = isolator?.phase_type
  if (phase && isolatorPhase) {
    // Three-pole isolators on single-phase supplies are acceptable when a
    // matching single-phase SKU is unavailable from the preferred supplier.
    if (phase === 'single_phase' && isolatorPhase === 'three_phase') {
      phaseCompatibility = 'good'
    } else {
      phaseCompatibility = phase === isolatorPhase ? 'good' : 'bad'
    }
  } else {
    if (!isolatorPhase) errors.push(doNotTranslate('isolator phase type not defined'))
    if (!phase) errors.push(doNotTranslate('inverter phase type not defined'))
  }

  const compatibility = errors.length
    ? null
    : currentCompatibility === 'good' && voltageCompatibility === 'good' && phaseCompatibility === 'good'
    ? 'good'
    : currentCompatibility === 'bad' || voltageCompatibility === 'bad' || phaseCompatibility === 'bad'
    ? 'bad'
    : 'ok'

  let issueCode: ElectricalIssueCode | undefined
  let errorMsg: string | undefined
  if (errors.length) {
    issueCode = 'AC_ISOLATOR_COMPAT_STATS_INCOMPLETE'
    errorMsg = doNotTranslate('Cannot calculate isolator compatibility') + ': ' + errors.join(', ')
  } else if (compatibility === 'bad') {
    if (phaseCompatibility === 'bad') {
      issueCode = 'AC_ISOLATOR_PHASE_MISMATCH'
      errorMsg = doNotTranslate('Isolator phases must match inverter phases.')
    } else if (currentCompatibility === 'bad') {
      issueCode = 'AC_ISOLATOR_CURRENT_MISMATCH'
      errorMsg = doNotTranslate('Isolator current rating must be >= inverter output current.')
    } else if (voltageCompatibility === 'bad') {
      issueCode = 'AC_ISOLATOR_VOLTAGE_MISMATCH'
      errorMsg = doNotTranslate('Isolator voltage rating must be >= supply voltage.')
    }
  }

  return {
    compatibility,
    data: {
      current,
      currentRating,
      currentCompatibility,
      voltage,
      voltageRating,
      voltageCompatibility,
      phase,
      isolatorPhase,
      phaseCompatibility,
      error: errorMsg,
      issueCode,
    },
  }
}

export function getSystemVoltageAndCurrent(strings: StringType[]) {
  let longestStringLength = 0
  strings.forEach((string) => {
    if (string.modules.length > longestStringLength) longestStringLength = string.modules.length
  })
  //@ts-ignore
  const voltage = window.editor.selectedSystem.userData.module.voc * longestStringLength
  //@ts-ignore
  const current = window.editor.selectedSystem.userData.module.isc * strings.length
  return { voltage, current }
}

// strings = window.editor.selectedSystem.userData.inverters.mppts[x].strings or an array with containing one string [strings[x]], depending if user has chosen to isolate the MPPT strings in parallel rather than each string separately
export function getDCIsolatorCompatibility(isolator: OtherComponentType, strings: StringType[]): resultsType {
  if (!isolator) {
    throw new Error(
      doNotTranslate('Cannot calculate DC isolator compatibility') + ': ' + doNotTranslate('isolator not defined')
    )
  }
  const { voltage, current } = getSystemVoltageAndCurrent(strings)
  if (!isolator.voltage_to_current_rating) {
    return {
      compatibility: null,
      data: {
        current,
        voltage,
        error:
          doNotTranslate('Cannot calculate isolator compatibility') +
          ': ' +
          doNotTranslate('component voltage to current rating not defined'),
        issueCode: 'DC_ISOLATOR_VTC_MATRIX_UNDEFINED' satisfies ElectricalIssueCode,
      },
    }
  }
  function getVoltageAndCurrentRating(voltageToCurrentRating, systemVoltage) {
    var voltageKey
    Object.keys(voltageToCurrentRating).forEach((voltage: string | number) => {
      // Find the lowest voltage in voltageToCurrentRating that's above our system voltage
      voltage = Number(voltage)
      if (!voltageKey || (voltage < voltageKey && systemVoltage < voltage)) {
        voltageKey = voltage
      }
    })
    return { voltage: voltageKey, current: voltageToCurrentRating[voltageKey] }
  }
  const rating = getVoltageAndCurrentRating(isolator.voltage_to_current_rating, voltage)
  const voltageRating = rating.voltage
  const currentRating = rating.current
  //@ts-ignore
  const voltageCompatibility = voltage < voltageRating ? 'good' : voltage * 1.25 < voltageRating ? 'ok' : 'bad'
  //@ts-ignore
  const currentCompatibility = current < currentRating ? 'good' : current * 1.25 < currentRating ? 'ok' : 'bad'
  const compatibility =
    voltageCompatibility === 'good' && currentCompatibility === 'good'
      ? 'good'
      : voltageCompatibility === 'bad' || currentCompatibility === 'bad'
      ? 'bad'
      : 'ok'

  let issueCode: ElectricalIssueCode | undefined
  let errorMsg: string | undefined
  if (compatibility === 'bad') {
    if (currentCompatibility === 'bad') {
      issueCode = 'DC_ISOLATOR_CURRENT_MISMATCH'
      errorMsg = doNotTranslate('DC isolator current rating must be >= the string current used for this check.')
    } else {
      issueCode = 'DC_ISOLATOR_VOLTAGE_MISMATCH'
      errorMsg = doNotTranslate('DC isolator voltage rating must be >= string Voc used for this check.')
    }
  }

  return {
    compatibility,
    data: {
      current,
      currentRating,
      currentCompatibility,
      voltage,
      voltageRating,
      voltageCompatibility,
      error: errorMsg,
      issueCode,
    },
  }
}
